import { generateHTML, generateJSON } from "@tiptap/html"
import { Node } from "@tiptap/pm/model"

import type { HunkRange } from "./proposal-steps"
import type { JSONContent } from "@tiptap/core"
import type { Schema } from "@tiptap/pm/model"
import {
	getProposalEditorExtensions,
	getProposalEditorSchema,
} from "../tiptap/proposal-schema"

export type SideBySideRowKind = "equal" | "insert" | "delete" | "replace"

export interface SideBySideCell {
	// Standalone HTML for the block, safe to render on its own. List items keep
	// their `<ul>`/`<ol>` wrapper so markers and indentation survive.
	html: string
	text: string
}

export interface SideBySideRow {
	kind: SideBySideRowKind
	left: SideBySideCell | null
	right: SideBySideCell | null
	// Proposals and hunks that produced this row, from the merged-doc positions
	// in `hunkRanges`. Empty on `equal` rows and whenever no ranges were passed.
	proposalIds: string[]
	hunkIds: string[]
}

interface Block {
	html: string
	text: string
	// Position span in the owning doc, used to attribute hunk ranges to a row.
	from: number
	to: number
	// Node type plus nesting, so a paragraph is never paired with a heading.
	signature: string
}

const LIST_TYPES = new Set(["bulletList", "orderedList", "taskList"])

// Articles past this many blocks skip the pairing pass; the quadratic LCS below
// is fine for a docs page and wasteful for a generated monster.
const MAX_BLOCKS = 600

export function computeSideBySideRows(
	baseHtml: string,
	mergedHtml: string,
	hunkRanges?: readonly HunkRange[] | null,
): SideBySideRow[] {
	const schema = getProposalEditorSchema()
	let baseBlocks: Block[]
	let mergedBlocks: Block[]
	try {
		const baseDoc = htmlToDoc(baseHtml, schema)
		const mergedDoc = htmlToDoc(mergedHtml, schema)
		// Count first: serializing every block to standalone HTML costs seconds
		// on a monster document, and the guard below throws that work away.
		if (
			countBlocks(baseDoc) > MAX_BLOCKS ||
			countBlocks(mergedDoc) > MAX_BLOCKS
		) {
			return []
		}
		// A blank document is no blocks, so a new article (or a deletion) never
		// pairs its first paragraph against a struck empty one.
		baseBlocks = baseHtml.trim() ? toBlocks(baseDoc, schema) : []
		mergedBlocks = mergedHtml.trim() ? toBlocks(mergedDoc, schema) : []
	} catch {
		return []
	}

	const rows = pairBlocks(baseBlocks, mergedBlocks)
	attributeHunks(rows, mergedBlocks, hunkRanges ?? [])
	return rows
}

// Which rows carry a change, in render order. The review header uses this for
// the "N changes" count and the next/previous change jump.
export function changedRowIndexes(rows: readonly SideBySideRow[]): number[] {
	const out: number[] = []
	rows.forEach((row, index) => {
		if (row.kind !== "equal") out.push(index)
	})
	return out
}

function htmlToDoc(html: string, schema: Schema): Node {
	return Node.fromJSON(
		schema,
		generateJSON(html || "<p></p>", getProposalEditorExtensions()),
	)
}

function nodeToHtml(node: Node, schema: Schema): string {
	const doc = schema.topNodeType.create(null, [node])
	return generateHTML(
		doc.toJSON() as JSONContent,
		getProposalEditorExtensions(),
	)
}

// Same flattening rule as `toBlocks`, without the HTML serialization.
function countBlocks(doc: Node): number {
	let count = 0
	doc.forEach((node) => {
		count +=
			LIST_TYPES.has(node.type.name) && node.childCount > 0
				? node.childCount
				: 1
	})
	return count
}

// Top-level blocks, with list containers unwrapped one level so a single
// changed bullet is one row rather than the whole list turning red.
function toBlocks(doc: Node, schema: Schema): Block[] {
	const blocks: Block[] = []
	doc.forEach((node, offset) => {
		const from = offset
		if (LIST_TYPES.has(node.type.name) && node.childCount > 0) {
			let itemOffset = from + 1
			node.forEach((item, _, index) => {
				blocks.push({
					html: listItemHtml(node, item, index, schema),
					text: blockText(item),
					from: itemOffset,
					to: itemOffset + item.nodeSize,
					signature: `${node.type.name}>${item.type.name}`,
				})
				itemOffset += item.nodeSize
			})
			return
		}
		blocks.push({
			html: nodeToHtml(node, schema),
			text: blockText(node),
			from,
			to: from + node.nodeSize,
			signature: node.type.name,
		})
	})
	return blocks
}

// A split-out item keeps its place in the numbering through `start`; the
// ordered-list extension renders the counter offset a steps list needs from it.
function listItemHtml(
	list: Node,
	item: Node,
	index: number,
	schema: Schema,
): string {
	if (list.type.name !== "orderedList") {
		return nodeToHtml(list.type.create(list.attrs, [item]), schema)
	}
	const start = (Number(list.attrs.start) || 1) + index
	return nodeToHtml(list.type.create({ ...list.attrs, start }, [item]), schema)
}

function normalize(text: string): string {
	return text.replace(/\s+/g, " ").trim()
}

// `textContent` runs the blocks of a tabs, code-group or table node together, so
// the last word of one paragraph fuses with the first of the next. The word diff
// reads this against text it collects block by block, and every fused pair marked
// both sides as changed.
function blockText(node: Node): string {
	return normalize(node.textBetween(0, node.content.size, " "))
}

// A block with no text (an image, a divider, an embed) has to compare on its
// markup, or every one of them would look identical to every other.
function matchKey(block: Block): string {
	return `${block.signature} ${block.text || block.html}`
}

function pairBlocks(left: Block[], right: Block[]): SideBySideRow[] {
	const common = longestCommonSubsequence(
		left.map(matchKey),
		right.map(matchKey),
	)
	const rows: SideBySideRow[] = []
	let l = 0
	let r = 0

	const flushGap = (untilL: number, untilR: number) => {
		const removed = left.slice(l, untilL)
		const added = right.slice(r, untilR)
		const paired = Math.min(removed.length, added.length)
		for (let i = 0; i < paired; i++) {
			rows.push(makeRow("replace", removed[i], added[i]))
		}
		for (let i = paired; i < removed.length; i++) {
			rows.push(makeRow("delete", removed[i], undefined))
		}
		for (let i = paired; i < added.length; i++) {
			rows.push(makeRow("insert", undefined, added[i]))
		}
	}

	for (const pair of common) {
		flushGap(pair.left, pair.right)
		rows.push(makeRow("equal", left[pair.left], right[pair.right]))
		l = pair.left + 1
		r = pair.right + 1
	}
	flushGap(left.length, right.length)

	return rows
}

function makeRow(
	kind: SideBySideRowKind,
	left: Block | undefined,
	right: Block | undefined,
): SideBySideRow {
	return {
		kind,
		left: left ? { html: left.html, text: left.text } : null,
		right: right ? { html: right.html, text: right.text } : null,
		proposalIds: [],
		hunkIds: [],
	}
}

function longestCommonSubsequence(
	a: readonly string[],
	b: readonly string[],
): { left: number; right: number }[] {
	const n = a.length
	const m = b.length
	// table[i][j] = LCS length of a[i..] and b[j..]
	const table: number[][] = Array.from({ length: n + 1 }, () =>
		new Array<number>(m + 1).fill(0),
	)
	for (let i = n - 1; i >= 0; i--) {
		for (let j = m - 1; j >= 0; j--) {
			const row = table[i]
			const nextRow = table[i + 1]
			if (!row || !nextRow) continue
			row[j] =
				a[i] === b[j]
					? (nextRow[j + 1] ?? 0) + 1
					: Math.max(nextRow[j] ?? 0, row[j + 1] ?? 0)
		}
	}

	const pairs: { left: number; right: number }[] = []
	let i = 0
	let j = 0
	while (i < n && j < m) {
		if (a[i] === b[j]) {
			pairs.push({ left: i, right: j })
			i++
			j++
			continue
		}
		const down = table[i + 1]?.[j] ?? 0
		const across = table[i]?.[j + 1] ?? 0
		if (down >= across) i++
		else j++
	}
	return pairs
}

// Hunk ranges are positions in the merged doc, so walk the merged blocks in
// render order and hand each row the hunks whose range overlaps its block.
function attributeHunks(
	rows: SideBySideRow[],
	mergedBlocks: Block[],
	hunkRanges: readonly HunkRange[],
): void {
	if (hunkRanges.length === 0) return

	let blockIndex = 0
	for (const row of rows) {
		if (!row.right) continue
		const block = mergedBlocks[blockIndex]
		blockIndex += 1
		if (!block || row.kind === "equal") continue

		const proposalIds = new Set<string>()
		const hunkIds = new Set<string>()
		for (const { proposalId, hunkId, range } of hunkRanges) {
			const from = range.kind === "delete" ? range.pos : range.from
			const to = range.kind === "delete" ? range.pos : range.to
			if (to < block.from || from > block.to) continue
			proposalIds.add(proposalId)
			hunkIds.add(hunkId)
		}
		row.proposalIds = [...proposalIds]
		row.hunkIds = [...hunkIds]
	}
}
